import PropTypes from "prop-types";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { FilterSwitchGroup } from "./FilterSwitchGroup";
import { Card } from "./Card";
import { useTheme as useCustomTheme } from "../theme/useTheme";

const TABS_CONTAINER_SX = { flexWrap: "nowrap" };
const TABS_OPTIONS_SX = { flexWrap: "nowrap" };

const ROW_HOVER_SCALE = 1.01;

const trackFor = (column) => column.width || "minmax(72px, 1fr)";

export function AnalyticsTable({
  title,
  subtitle,
  actions,
  tabs,
  tabsInHeader = false,
  activeTab,
  onTabChange,
  tabsDisabled = false,
  columns,
  rows,
  getRowKey,
  onRowClick,
  renderRowDetail,
  isRowExpanded,
  isLoading = false,
  skeletonRows = 5,
  emptyState,
  footer,
  minTableWidth = 520,
  sx,
}) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();

  const gridTemplateColumns = columns.map(trackFor).join(" ");
  const hasRows = rows?.length > 0;
  const hasTabs = tabs?.length > 0;

  const tabsGroup = (
    <FilterSwitchGroup
      options={tabs}
      selectedValue={activeTab}
      onChange={onTabChange}
      disabled={tabsDisabled}
      containerSx={TABS_CONTAINER_SX}
      optionsSx={TABS_OPTIONS_SX}
    />
  );
  const showEmpty = !isLoading && !hasRows && emptyState;

  const rowBaseSx = {
    display: "grid",
    gridTemplateColumns,
    alignItems: "center",
    columnGap: 2,
    paddingX: 1.5,
  };

  const alignFor = (column) => (column.align === "right" ? "right" : "left");
  const justifyFor = (column) =>
    column.align === "right" ? "flex-end" : "flex-start";

  const cellSx = (column) => ({
    minWidth: 0,
    textAlign: alignFor(column),
    display: "flex",
    alignItems: "center",
    justifyContent: justifyFor(column),
    fontSize: "0.875rem",
    fontWeight: column.highlighted ? 700 : 400,
    color: column.highlighted ? "text.primary" : "text.secondary",
    ...(column.cellSx || {}),
  });

  return (
    <Card sx={{ gap: 0, height: "100%", minWidth: 0, ...sx }}>
      {/* Header */}
      <Box sx={{ paddingX: { xs: 2, sm: 3 }, paddingTop: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          spacing={1.5}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {(actions || (tabsInHeader && hasTabs)) && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flexShrink: 0 }}
            >
              {tabsInHeader && hasTabs && (
                <Box sx={{ maxWidth: "100%", overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
                  {tabsGroup}
                </Box>
              )}
              {actions}
            </Stack>
          )}
        </Stack>

        {hasTabs && !tabsInHeader && (
          <Box
            sx={{
              marginTop: 2,
              overflowX: "auto",
              overflowY: "hidden",
              maxWidth: "100%",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {tabsGroup}
          </Box>
        )}
      </Box>

      {showEmpty ? (
        <Box sx={{ paddingX: { xs: 2, sm: 3 }, paddingBottom: 2 }}>{emptyState}</Box>
      ) : (
        <Box
          sx={{
            overflowX: "auto",
            paddingX: { xs: 1, sm: 1.5 },
            paddingTop: 1.5,
            paddingBottom: 1,
          }}
        >
          <Box role="table" sx={{ minWidth: minTableWidth }}>
            {/* Column headers */}
            <Box
              role="row"
              sx={{
                ...rowBaseSx,
                paddingY: 1,
                borderBottom: `1px solid ${neutrals.gray[300]}`,
              }}
            >
              {columns.map((column) => (
                <Box
                  key={column.key}
                  role="columnheader"
                  sx={{
                    minWidth: 0,
                    textAlign: alignFor(column),
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    color: column.highlighted
                      ? theme.palette.text.primary
                      : theme.palette.text.secondary,
                  }}
                >
                  {column.header}
                </Box>
              ))}
            </Box>

            {/* Rows */}
            {isLoading
              ? Array.from({ length: skeletonRows }).map((_, index) => (
                  <Box
                    key={`skeleton-${index}`}
                    role="row"
                    sx={{
                      ...rowBaseSx,
                      paddingY: 1.5,
                      borderBottom:
                        index === skeletonRows - 1
                          ? "none"
                          : `1px solid ${neutrals.gray[200]}`,
                    }}
                  >
                    {columns.map((column, columnIndex) => (
                      <Box key={column.key} sx={cellSx(column)}>
                        <Skeleton
                          variant="text"
                          width={columnIndex === 0 ? "70%" : 44}
                        />
                      </Box>
                    ))}
                  </Box>
                ))
              : rows.map((row, index) => {
                  const key = getRowKey ? getRowKey(row, index) : index;
                  const expanded = Boolean(isRowExpanded?.(row, index));
                  const clickable = Boolean(onRowClick);
                  // An expanded row keeps its detail panel attached to it, so
                  // the two are wrapped together and only the pair carries the
                  // separator the row would otherwise draw.
                  const isLast = index === rows.length - 1;

                  return (
                    <Box key={key}>
                      <Box
                        role="row"
                        tabIndex={clickable ? 0 : undefined}
                        aria-expanded={renderRowDetail ? expanded : undefined}
                        onClick={clickable ? () => onRowClick(row, index) : undefined}
                        onKeyDown={
                          clickable
                            ? (event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  onRowClick(row, index);
                                }
                              }
                            : undefined
                        }
                        sx={{
                          ...rowBaseSx,
                          position: "relative",
                          paddingY: 1.25,
                          minHeight: 48,
                          cursor: clickable ? "pointer" : "default",
                          borderBottom: `1px solid ${
                            isLast || expanded ? "transparent" : neutrals.gray[200]
                          }`,
                          backgroundColor: expanded
                            ? theme.palette.action.hover
                            : "transparent",
                          transformOrigin: "center",
                          transition:
                            "transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease",
                          "&:hover": {
                            zIndex: 1,
                            // A row that is already open must not lift away
                            // from the panel hanging under it.
                            transform: expanded
                              ? "none"
                              : `scale(${ROW_HOVER_SCALE})`,
                            backgroundColor: theme.palette.action.hover,
                            borderBottomColor: "transparent",
                            boxShadow: expanded
                              ? "none"
                              : "0 6px 18px -8px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15, 23, 42, 0.04)",
                          },
                          "&:focus-visible": {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: -2,
                          },
                          "@media (prefers-reduced-motion: reduce)": {
                            transition: "background-color 120ms ease",
                            "&:hover": { transform: "none" },
                          },
                        }}
                      >
                        {columns.map((column) => (
                          <Box key={column.key} role="cell" sx={cellSx(column)}>
                            {column.render
                              ? column.render(row, index)
                              : row[column.key]}
                          </Box>
                        ))}
                      </Box>

                      {expanded && renderRowDetail && (
                        <Box
                          sx={{
                            paddingX: { xs: 0.5, sm: 1.5 },
                            paddingBottom: 2,
                            borderBottom: isLast
                              ? "none"
                              : `1px solid ${neutrals.gray[200]}`,
                          }}
                        >
                          {renderRowDetail(row, index)}
                        </Box>
                      )}
                    </Box>
                  );
                })}
          </Box>
        </Box>
      )}

      {footer && !showEmpty && (
        <Box sx={{ paddingX: { xs: 2, sm: 3 }, paddingBottom: 2 }}>{footer}</Box>
      )}

      {!footer && !showEmpty && <Box sx={{ height: 8 }} />}
    </Card>
  );
}

AnalyticsTable.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  tabsInHeader: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.any.isRequired, label: PropTypes.node })
  ),
  activeTab: PropTypes.any,
  onTabChange: PropTypes.func,
  tabsDisabled: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.node,
      align: PropTypes.oneOf(["left", "right"]),
      width: PropTypes.string,
      highlighted: PropTypes.bool,
      render: PropTypes.func,
      cellSx: PropTypes.object,
    })
  ).isRequired,
  rows: PropTypes.array,
  getRowKey: PropTypes.func,
  onRowClick: PropTypes.func,
  renderRowDetail: PropTypes.func,
  isRowExpanded: PropTypes.func,
  isLoading: PropTypes.bool,
  skeletonRows: PropTypes.number,
  emptyState: PropTypes.node,
  footer: PropTypes.node,
  minTableWidth: PropTypes.number,
  sx: PropTypes.object,
};

export default AnalyticsTable;
